import React from 'react'
import env from './../../environments/environment'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import AwardLevelsShowcase from './components/AwardLevelsShowcase'
import ContentHtml from '../../components/content/ContentHtml/ContentHtml.component'
import SpecialtyPoints from './components/SpecialtyPoints'
import PointsBreakdown from './components/PointsBreakdown'
import CoverageService from './../../shared/services/Coverage.service'
import CarrierService from './../../shared/services/Carrier.service'
import PointOverrideService from './../../shared/services/PointOverride.service'
import PointService from './../../shared/services/Point.service'
import PointsRedemtion from './components/PointsRedemption'

import './PointsDetailsPage.scss'

const SHOW_MERGED_UNIQUE = true

const fetchPointDefinitions = async () => {
  let points = [],
    coverageIds = [],
    uniquePointDefs = [],
    nonuniquePointDefs = []

  try {
    points = await PointService.search({ pagination: false })
    points = points && points?.models ? points.models : []
  } catch (ex) {
    console.error('ERROR: Failed to fetch points in PointsDetailsPage.', ex)
  }

  if (points && points.length) {
    coverageIds =
      points && Array.isArray(points)
        ? points.map(point => parseInt(point.coverage_id)).filter((val, idx, self) => self.indexOf(val) === idx)
        : []
    uniquePointDefs = points.filter(p => parseInt(p.is_unique) === 1)
    nonuniquePointDefs = points.filter(p => parseInt(p.is_unique) !== 1)
  }

  return {
    points,
    coverageIds,
    uniquePointDefs,
    nonuniquePointDefs,
  }
}

const fetchCoveragesById = async coverageIds => {
  try {
    let coverages = await CoverageService.search({
      pagination: false,
      search: { id: coverageIds, coverage_active: 1 },
    })
    return coverages && coverages?.models ? coverages.models : []
  } catch (ex) {
    console.error('ERROR: Failed to fetch coverages in PointsDetailsPage.', ex)
    return []
  }
}

const fetchCarriersById = async carrierIds => {
  try {
    let carriers = await CarrierService.search({
      pagination: false,
      search: { id: [0].concat(carrierIds) },
    })
    return carriers && carriers?.models ? carriers.models : []
  } catch (ex) {
    console.error('ERROR: Failed to fetch carriers in PointsDetailsPage.', ex)
    return []
  }
}

const findUniqueDefinitions = (coverage, carriers, uniqueDefs) => {
  if (!uniqueDefs || !uniqueDefs.length) return null

  return carriers
    .map(c => {
      let points = uniqueDefs.find(p => parseInt(p.carrier_id) === parseInt(c.id))
      return !points ? null : { ...c, points }
    })
    .filter(c => c)
}

class PointsDetailsPage extends React.Component {
  state = {
    isLoadingCoverages: false,
    isLoadingSpecialty: false,
    items: { vertical: '1' },
    carriers: [],
    coverages: [],
    overrides: [],
    unique: [],
  }

  togglePills = (type, tab) => e => {
    e.preventDefault()
    if (this.state.items[type] !== tab) {
      let items = { ...this.state.items }
      items[type] = tab
      this.setState({ items })
    }
  }

  fetchCoveragePoints = async () => {
    if (this.state.isLoadingCoverages) return

    this.setState({ isLoadingCoverages: true })

    let { points, coverageIds, uniquePointDefs, nonuniquePointDefs } = await fetchPointDefinitions(),
      coverages = await fetchCoveragesById(coverageIds),
      unique = await fetchCarriersById(
        uniquePointDefs.map(p => p.carrier_id).filter((val, idx, self) => self.indexOf(val) === idx)
      )

    // Define unique carrier coverage w/ points.
    unique = unique
      .map(carrier => {
        carrier.points = uniquePointDefs.filter(p => parseInt(p.carrier_id) === parseInt(carrier.id))
        carrier.points = carrier.points.map(p => {
          let coverage = coverages.find(c => parseInt(c.id) === parseInt(p.coverage_id))
          p.coverage_name = coverage?.coverage_name
          p.coverage_category = coverage?.coverage_category
          return p
        })
        carrier.points = carrier.points.sort((a, b) => a.coverage_name.localeCompare(b.coverage_name, 'en'))
        return carrier
      })
      .sort((a, b) => a.c_name.localeCompare(b.c_name, 'en'))

    // Define (non-unique) coverages w/ points for main detail table.
    coverages = coverages
      .map(coverage => {
        coverage.points = points.find(point => parseInt(point.coverage_id) === parseInt(coverage.id))

        // merge unique configurations to the non-unique coverages
        // for rendering on table.
        if (SHOW_MERGED_UNIQUE) {
          coverage.unique = findUniqueDefinitions(
            coverage,
            unique,
            uniquePointDefs.filter(p => parseInt(p.coverage_id) === parseInt(coverage.id))
          )
          if (!coverage.unique || !coverage.unique.length) coverage.unique = null
        }

        return coverage
      })
      .filter(coverage => !!coverage.points)
      .sort((a, b) => (a.coverage_name > b.coverage_name ? 1 : a.coverage_name < b.coverage_name ? -1 : 0))

    this.setState(
      SHOW_MERGED_UNIQUE ? { coverages, isLoadingCoverages: false } : { coverages, unique, isLoadingCoverages: false }
    )
  }

  fetchSpecialtyPoints = async () => {
    if (this.state.isLoadingSpecialty) return

    this.setState({ isLoadingSpecialty: true })

    let overrides = null

    try {
      overrides = await PointOverrideService.search({ pagination: false })
    } catch (ex) {
      console.error('ERROR: Failed to fetch point overrides in PointsDetailsPage.', ex)
      return false
    }

    overrides = (overrides && overrides?.models ? overrides.models : []).filter(o => o && !o.deleted_at)
    let carrierIds = (
      overrides && Array.isArray(overrides)
        ? overrides
            .filter(override => override.carrier_id !== null && !isNaN(override.carrier_id))
            .map(override => override.carrier_id)
        : []
    ).filter((val, idx, self) => self.indexOf(val) === idx)

    let carriers = null

    try {
      carriers = carrierIds.length
        ? await CarrierService.search({
            search: { id: carrierIds },
            pagination: false,
          })
        : []
    } catch (ex) {
      console.error('ERROR: Failed to fetch override carriers in PointsDetailsPage.', ex)
      return false
    }

    carriers = carriers && carriers?.models ? carriers.models : []

    this.setState({ overrides, carriers, isLoadingSpecialty: false })
  }

  componentDidMount() {
    this.fetchCoveragePoints()
    this.fetchSpecialtyPoints()
  }

  render() {
    return (
      <React.Fragment>
        <DashboardLayout>
          <main id='PointsDetailsPage' className='adminStyle mainSection'>
            <div className='container-fluid mt-3'>
              <div className='buck-logo pb-3 d-flex justify-content-center'>
                <img
                  src={`https://firebasestorage.googleapis.com/v0/b/${env.integrations.firebase.config.storageBucket}/o/static%2Flogos%2Fusabg-bucks.logo.png?alt=media`}
                  alt='buck-logo'
                />
              </div>
              <div className='card card-cascade narrower'>
                <div className='view view-cascade gradient-card-header blue-gradient'>
                  <h2 className='h2-responsive'>Points Details</h2>
                </div>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-12'>
                      <ul className='nav md-pills pills-primary justify-content-center'>
                        <li className='nav-item'>
                          <a
                            className={`nav-link ${this.state.items['vertical'] === '1' ? 'active' : ''}`}
                            href='/usabg-bucks-program'
                            onClick={this.togglePills('vertical', '1')}
                          >
                            Award Levels & Info
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className={`nav-link ${this.state.items['vertical'] === '2' ? 'active' : ''}`}
                            href='/usabg-bucks-program'
                            onClick={this.togglePills('vertical', '2')}
                          >
                            Points Details
                            {this.state.isLoadingCoverages || this.state.isLoadingSpecialty ? (
                              <>
                                &nbsp;
                                <span>
                                  <i className='fa fa-spin fa-spinner'></i>
                                </span>
                              </>
                            ) : (
                              ''
                            )}
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className={`nav-link ${this.state.items['vertical'] === '3' ? 'active' : ''}`}
                            href='/usabg-bucks-program'
                            onClick={this.togglePills('vertical', '3')}
                          >
                            USABG BUCK$ Redemption
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div className='col-12'>
                      <div className='tab-content'>
                        <div className={`tab-pane ${this.state.items['vertical'] === '1' ? 'active' : ''}`}>
                          <AwardLevelsShowcase />
                        </div>
                        <div className={`tab-pane ${this.state.items['vertical'] === '2' ? 'active' : ''}`}>
                          <div className='container-fluid pip-disclaimer'>
                            <div className='row'>
                              <div className='col-12'>
                                <div>
                                  <ContentHtml contentSlug='point-detials-disclaimer' editor={{ iconColor: 'white' }} />
                                </div>
                              </div>
                            </div>
                          </div>
                          <SpecialtyPoints
                            isLoading={this.state.isLoadingSpecialty}
                            coverages={this.state.coverages}
                            carriers={this.state.carriers}
                            uniques={this.state.unique}
                            overrides={this.state.overrides}
                          />
                          <PointsBreakdown isLoading={this.state.isLoadingCoverages} coverages={this.state.coverages} />
                        </div>
                        <div className={`tab-pane ${this.state.items['vertical'] === '3' ? 'active' : ''}`}>
                          <PointsRedemtion />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </DashboardLayout>
      </React.Fragment>
    )
  }
}

export default PointsDetailsPage
